import * as React from "react";
import { useEffect, useState } from "react";
import { useDataProvider } from "react-admin";
import { Typography, Box } from "@material-ui/core";
import { ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { SourceReliabilityTotalNff } from "./Constants";

interface TotalNffData {
  id: string;
  totalUnscheduledRemovals: number;
  totalCompletedRemovals: number;
  totalNff: number;
  totalNffRate: number;
}

const TotalNffRate = ({ filterParams }) => {
  const dataProvider = useDataProvider();
  const [totalData, setTotalData] = useState<TotalNffData | null>(null);

  useEffect(() => {
    GetTotalData();
  }, [filterParams]);

  const GetTotalData = async () => {
    await dataProvider
      .getList(SourceReliabilityTotalNff, {
        pagination: { page: 1, perPage: 1 },
        sort: { field: "id", order: "ASC" },
        filter: filterParams,
      })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setTotalData(data[0] as any);
        }
      })
      .catch(error => {
        console.log(error);
      });
  };

  // Don't render if no data
  if (!totalData) {
    return null;
  }

  // Calculate percentage ranges for the donut chart
  const rate = totalData.totalNffRate;

  // Determine color ranges
  const getColorForRate = (rate: number) => {
    if (rate <= 10) return "#4CAF50"; // Green: 0-10%
    if (rate <= 20) return "#FFC107"; // Yellow: 10-20%
    return "#E53935"; // Red: 20%+
  };

  // Create chart data for donut
  const chartData = [
    { name: "NFF Rate", value: rate, fill: getColorForRate(rate) },
    { name: "Non-NFF", value: 100 - rate, fill: "#E0E0E0" },
  ];

  // Legend data for the ranges
  const legendData = [
    { name: "0-10% Good", color: "#4CAF50" },
    { name: "10-20% Acceptable", color: "#FFC107" },
    { name: "20%+ Risk", color: "#E53935" },
  ];

  const CustomLegend = () => (
    <Box display="flex" justifyContent="center" alignItems="center" style={{ marginTop: 20 }}>
      {legendData.map((item, index) => (
        <Box key={index} display="flex" alignItems="center" style={{ marginRight: 20 }}>
          <div
            style={{
              width: 16,
              height: 16,
              backgroundColor: item.color,
              marginRight: 8,
              borderRadius: 2,
            }}
          />
          <Typography variant="body2">{item.name}</Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <Typography gutterBottom variant="h5" component="h2" align="center" style={{ marginTop: 16, marginBottom: 24 }}>
        Total NFF Limit
      </Typography>
      <Typography
        variant="body2"
        align="center"
        style={{ color: "#888", fontStyle: "italic", marginBottom: 16, padding: "0 16px" }}
      >
        This page is for information only. Final NFF rates are going to be calculated by the component reliability team
        before annual reviews
      </Typography>
      <div style={{ width: "100%", height: 320, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div style={{ width: 300, height: "100%", position: "relative" }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={65}
                outerRadius={100}
                paddingAngle={0}
                dataKey="value"
                startAngle={90}
                endAngle={-270}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          {/* Center text overlay */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
            }}
          >
            <Typography variant="h3" style={{ fontWeight: "bold", color: getColorForRate(rate), fontSize: "2.5rem" }}>
              {rate.toFixed(1)}%
            </Typography>
            <Typography variant="body2" style={{ color: "#666", marginTop: 4, fontSize: "0.75rem" }}>
              NFF RATE
            </Typography>
          </div>
        </div>
      </div>
      <CustomLegend />

      {/* Additional stats */}
      <Box display="flex" justifyContent="center" style={{ marginTop: 20, gap: 40 }}>
        <Box textAlign="center">
          <Typography variant="body2" color="textSecondary">
            Total Unscheduled Removals
          </Typography>
          <Typography variant="h6" style={{ fontWeight: "bold" }}>
            {totalData.totalUnscheduledRemovals}
          </Typography>
        </Box>
        <Box textAlign="center">
          <Typography variant="body2" color="textSecondary">
            Total NFF
          </Typography>
          <Typography variant="h6" style={{ fontWeight: "bold", color: "#E53935" }}>
            {totalData.totalNff}
          </Typography>
        </Box>
        <Box textAlign="center">
          <Typography variant="body2" color="textSecondary">
            Total Completed Removals
          </Typography>
          <Typography variant="h6" style={{ fontWeight: "bold" }}>
            {totalData.totalCompletedRemovals}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default TotalNffRate;
